import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Pressable, Text, View, type ViewStyle } from 'react-native'

import type { TagTarget } from '../../common/eventTags'
import { GlassView } from '../components/GlassView'
import { Icon } from '../components/Icon'
import { PillButton } from '../components/PillTabs'
import { lstrings, sprintf } from '../locales/strings'
import {
  InstructionToast,
  type ScoreboardBounds,
  type SetupToast
} from '../scoring/InstructionToast'
import {
  CONTROL_COLORS,
  CONTROL_DECK_HEIGHT,
  CONTROL_GAP,
  CONTROL_HEIGHT,
  ControlBackdrop,
  ControlFace,
  controlFrame,
  ControlIcon,
  DICTATION_LOG_HEIGHT
} from '../scoring/ScoringControlAppearance'
import {
  type HistoryBounds,
  HistoryPopup,
  type HistoryRow
} from '../scoring/ScoringHistory'
import { SwitchHitterHint } from '../scoring/SwitchHitterHint'
import { UndoTicker } from '../scoring/UndoTicker'
import { useHoldSelection } from '../scoring/useHoldSelection'
import { useMenuEscape } from '../scoring/useMenuEscape'
import { useTheme } from '../theme/ThemeContext'
import { FieldCanvas } from './FieldCanvas'
import {
  compactToastBounds,
  type MenuButtonEdges,
  menuButtonEdges,
  toastTopBelow,
  WEB_MENU_BUTTON
} from './fieldSafeArea'
import type { BaseProjection, FieldCanvasProps } from './fieldTypes'
import { useFieldSurface } from './useFieldSurface'

/** The scoring screen's camera, which the replay frames its field with too. */
export const SCORING_CAMERA = {
  zoom: 1.25,
  cameraTilt: (-11 * Math.PI) / 180,
  cameraHeight: 33
} as const

export const PITCH_CONTROLS = [
  { kind: 'ball', label: 'Ball' },
  { kind: 'called', label: 'Called Strike' },
  { kind: 'swing', label: 'Swing and Miss' },
  { kind: 'foul', label: 'Foul Ball' },
  { kind: 'play', label: 'Ball in Play' }
] as const
export interface BaseballScreenProps {
  suspended?: boolean
  setupToast?: SetupToast
  field: FieldCanvasProps
  onMenu: () => void
  /**
   * Where ☰ ends up, measured: on a phone it sits at the safe inset rather
   * than the web's 8, and whatever opens beside it must clear it.
   */
  onMenuButton?: (edges: MenuButtonEdges) => void
  onUndo: () => void
  onPitch: (kind: (typeof PITCH_CONTROLS)[number]['kind']) => void
  historyRows?: HistoryRow[]
  /** Undo wording only when its action produced the newest tag target. */
  tagHistoryDetail?: { primary: string; secondary?: string }
  onHistoryOpen?: () => void
  onHistoryClose?: () => void
  onTagSelect?: (target: TagTarget) => void
  historyLabel?: string
  /** The last play in two lines, shown in place of the label and the hint. */
  historyDetail?: { primary: string; secondary?: string }
  canPitch: boolean
  enabledPitchKinds?: ReadonlyArray<(typeof PITCH_CONTROLS)[number]['kind']>
  remainingMs?: number
  instruction?: string
  instructionStatus?: string
  instructionDetail?: string
  finishLabel?: string
  canFinish?: boolean
  onFinish?: () => void
  onCameraGesture?: (active: boolean) => void
  summary: string
  accessibilitySummary?: string
  children?: React.ReactNode
  ballInPlayControl?: React.ReactNode
  moreControl?: React.ReactNode
  /**
   * The batter's walk-up note, in a row of its own directly above the row
   * More starts. Only a phone passes one.
   */
  walkUpControl?: React.ReactNode
  /** Sits directly above the column that holds Bunt and Swing. */
  dictationControl?: React.ReactNode
  /**
   * Sits in the top-left corner, ending left of the menu button; with the
   * speaker, between it and the menu button.
   */
  dictationLog?: React.ReactNode
  /**
   * The speaker at the top-left corner, opposite ☰: the game's sounds, on
   * or off. Walk-up songs are not its to stop.
   */
  sound?: { on: boolean; onToggle: () => void }
  onDismissHint?: () => void
  /**
   * Points kept clear across the top for a line drawn there (the scoring
   * screen's offline line): the field, its scoreboard and the header row
   * start that much lower. 0, the default, is the layout without one.
   */
  topBand?: number
}

/**
 * The field's holder under a band across the top: moved down, keeping its
 * size, so the GL canvas never lays itself out again (a resize blanks a
 * frame) and its bottom runs under the deck, which a phone has room for.
 */
export function fieldHolderStyle(band: number): ViewStyle {
  return band === 0
    ? { flex: 1 }
    : { flex: 1, transform: [{ translateY: band }] }
}

/**
 * The canvas's own callbacks, with every screen point they report moved down
 * by the band the canvas sits under: the bases, a runner's Safe and Out
 * targets and a held runner's anchor, which the controls over the field draw
 * from. Ground points are the field's own and need nothing.
 */
export function bandedField(
  field: FieldCanvasProps,
  band: number
): FieldCanvasProps {
  if (band === 0) return field
  const down = <T extends { y: number }>(point: T): T => ({
    ...point,
    y: point.y + band
  })
  const { onBaseProjection, onRunnerDrag, onRunnerHold } = field
  return {
    ...field,
    ...(onBaseProjection == null
      ? {}
      : {
          onBaseProjection: projection => {
            onBaseProjection({
              ...projection,
              bases: projection.bases.map(down),
              ...(projection.heads == null
                ? {}
                : { heads: projection.heads.map(down) }),
              ...(projection.feet == null
                ? {}
                : { feet: projection.feet.map(down) })
            })
          }
        }),
    ...(onRunnerDrag == null
      ? {}
      : {
          onRunnerDrag: view => {
            onRunnerDrag(
              view == null
                ? null
                : { ...view, safe: down(view.safe), out: down(view.out) }
            )
          }
        }),
    ...(onRunnerHold == null
      ? {}
      : {
          onRunnerHold: (id, anchor) => {
            onRunnerHold(id, down(anchor))
          }
        })
  }
}

/** The board the canvas reports, moved down by the band its canvas sits under. */
export function bandedBounds(
  canvas: ScoreboardBounds,
  band: number
): ScoreboardBounds {
  return band === 0 ? canvas : { ...canvas, y: canvas.y + band }
}

/**
 * The canvas's steady reports, its bases and its board, moved down by the
 * band. The canvas reports only when its own values change, and a band moves
 * the canvas without changing them, so the last of each, kept as the canvas
 * gave it, is moved again whenever the band comes or goes.
 */
export function useBandedReports(
  field: FieldCanvasProps,
  band: number
): {
  field: FieldCanvasProps
  bounds: ScoreboardBounds | undefined
  onScoreboardBounds: (canvas: ScoreboardBounds) => void
} {
  const [bounds, setBounds] = useState<ScoreboardBounds>()
  const canvasBoard = useRef<ScoreboardBounds>(undefined)
  const canvasBases = useRef<BaseProjection>(undefined)
  const onScoreboardBounds = useCallback(
    (canvas: ScoreboardBounds) => {
      canvasBoard.current = canvas
      setBounds(bandedBounds(canvas, band))
    },
    [band]
  )
  const banded = bandedField(field, band)
  const report = banded.onBaseProjection
  const latest = useRef(report)
  latest.current = report
  useEffect(() => {
    if (canvasBoard.current != null)
      setBounds(bandedBounds(canvasBoard.current, band))
    if (canvasBases.current != null) latest.current?.(canvasBases.current)
  }, [band])
  return {
    field:
      report == null
        ? banded
        : {
            ...banded,
            onBaseProjection: projection => {
              canvasBases.current = projection
              report(projection)
            }
          },
    bounds,
    onScoreboardBounds
  }
}

/**
 * The safe area's padding with `band` more at the top: added on a phone,
 * whose inset is a number, and through `calc()` on the web, whose inset is
 * the browser's `env()`.
 */
export function bandedSafe(safe: ViewStyle, band: number): ViewStyle {
  if (band === 0) return safe
  const top = safe.paddingTop
  return {
    ...safe,
    paddingTop:
      typeof top === 'number'
        ? top + band
        : top == null
          ? `${band}px`
          : `calc(${String(top)} + ${band}px)`
  }
}
/** Immersive scoring surface, with one fixed responsive camera. */
export function BaseballScreen(props: BaseballScreenProps): React.ReactElement {
  const [historyBounds, setHistoryBounds] = useState<HistoryBounds | null>(null)
  const undoButton = useRef<React.ComponentRef<typeof View>>(null)
  const {
    onPressIn: handleUndoPressIn,
    onPressOut: handleUndoPressOut,
    onLongPress: handleUndoLongPress
  } = useHoldSelection()
  const { onHistoryClose } = props
  const closeHistory = useCallback(() => {
    setHistoryBounds(null)
    onHistoryClose?.()
  }, [onHistoryClose])
  useMenuEscape(
    historyBounds != null && props.suspended !== true,
    closeHistory,
    `Undo ${props.historyLabel ?? ''}`
  )
  useEffect(() => {
    if (props.historyLabel == null) closeHistory()
  }, [props.historyLabel, closeHistory])
  const surface = useFieldSurface()
  const theme = useTheme()
  const band = props.topBand ?? 0
  const {
    field: bandedCanvas,
    bounds,
    onScoreboardBounds
  } = useBandedReports(props.field, band)
  const [viewportHeight, setViewportHeight] = useState(0)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [header, setHeader] = useState<ScoreboardBounds>()
  const menuButton =
    header == null || viewportWidth === 0
      ? WEB_MENU_BUTTON
      : menuButtonEdges(header, viewportWidth)
  const { onMenuButton } = props
  const { bottom: menuBottom, right: menuRight } = menuButton
  useEffect(() => {
    onMenuButton?.({ bottom: menuBottom, right: menuRight })
  }, [menuBottom, menuRight, onMenuButton])
  const compact = viewportHeight > 0 && viewportHeight < 480 && header != null
  const toastBounds = compact
    ? compactToastBounds(header, props.sound != null)
    : bounds
  const button = { ...controlFrame, flex: 1 } as const
  return (
    <View
      style={[surface.root, { backgroundColor: theme.backgroundColor }]}
      onLayout={event => {
        setViewportHeight(event.nativeEvent.layout.height)
        setViewportWidth(event.nativeEvent.layout.width)
      }}
    >
      {historyBounds != null && (
        <HistoryPopup
          viewport={{ width: viewportWidth, height: viewportHeight }}
          bounds={historyBounds}
          rows={(props.historyRows ?? []).slice(-6)}
          onClose={closeHistory}
          onSelect={target => {
            closeHistory()
            props.onTagSelect?.(target)
          }}
        />
      )}
      <View style={fieldHolderStyle(band)}>
        {props.suspended !== true && (
          <FieldCanvas
            {...bandedCanvas}
            {...SCORING_CAMERA}
            bottomExtension={112}
            onScoreboardBounds={onScoreboardBounds}
          />
        )}
      </View>
      <View
        pointerEvents="box-none"
        style={[
          { position: 'absolute', inset: 0, justifyContent: 'space-between' },
          bandedSafe(surface.safe, band)
        ]}
      >
        <View
          pointerEvents="box-none"
          style={{ flexDirection: 'row', justifyContent: 'flex-end' }}
          onLayout={event => {
            setHeader(event.nativeEvent.layout)
          }}
        >
          <Text
            accessibilityLiveRegion="polite"
            accessibilityLabel={props.accessibilitySummary ?? props.summary}
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              opacity: 0
            }}
          >
            {props.summary}
          </Text>
          {props.sound != null && (
            <GlassView
              radius={24}
              interactive
              androidBlur
              style={{
                width: 48,
                height: 48,
                borderRadius: 24,
                overflow: 'hidden',
                marginRight: 8
              }}
            >
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={
                  props.sound.on ? 'Mute game sounds' : 'Unmute game sounds'
                }
                onPress={() => {
                  props.sound?.onToggle()
                }}
                style={{
                  width: 48,
                  height: 48,
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Icon
                  name={props.sound.on ? 'volume_up' : 'volume_off'}
                  size={1.5}
                  color={
                    props.sound.on
                      ? CONTROL_COLORS.micOn.text
                      : CONTROL_COLORS.micOff.text
                  }
                />
              </Pressable>
            </GlassView>
          )}
          {(props.dictationLog != null || props.sound != null) && (
            <View
              pointerEvents="box-none"
              style={{ flex: 1, minWidth: 0, height: 48, marginRight: 8 }}
            >
              <View
                pointerEvents="box-none"
                style={{ position: 'absolute', top: 0, left: 0, right: 0 }}
              >
                {props.dictationLog}
              </View>
            </View>
          )}
          <GlassView
            radius={24}
            interactive
            // Real glass over the field: iOS's own, Android's backdrop blur,
            // and the web's backdrop-filter.
            androidBlur
            style={{
              width: 48,
              height: 48,
              borderRadius: 24,
              overflow: 'hidden'
            }}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Open scoring menu"
              onPress={props.onMenu}
              style={{
                width: 48,
                height: 48,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Text
                selectable={false}
                style={{ color: theme.primaryTextColor, fontSize: 26 }}
              >
                ☰
              </Text>
            </Pressable>
          </GlassView>
        </View>
        <InstructionToast
          setup={props.setupToast}
          instruction={props.instruction}
          status={props.instructionStatus}
          detail={props.instructionDetail}
          finishLabel={props.finishLabel}
          remainingMs={props.remainingMs}
          canFinish={props.canFinish}
          onFinish={props.onFinish}
          bounds={toastBounds}
          compact={compact}
          minTop={toastTopBelow(menuButton)}
          below={props.dictationLog == null ? 0 : DICTATION_LOG_HEIGHT + 8}
        />
        {props.onDismissHint != null && (
          <SwitchHitterHint bounds={bounds} onDismiss={props.onDismissHint} />
        )}
        {props.children}
        <View pointerEvents="box-none">
          {props.walkUpControl != null && (
            <View
              pointerEvents="box-none"
              testID="walk-up-row"
              style={{
                flexDirection: 'row',
                gap: 6,
                height: CONTROL_HEIGHT,
                marginBottom: CONTROL_GAP
              }}
            >
              <View
                pointerEvents="box-none"
                style={{
                  flex: 4,
                  flexBasis: 18,
                  flexDirection: 'row',
                  gap: 6,
                  minWidth: 0
                }}
              >
                <View pointerEvents="box-none" style={{ flex: 1, minWidth: 0 }}>
                  {props.walkUpControl}
                </View>
                <View pointerEvents="none" style={{ flex: 3, flexBasis: 12 }} />
              </View>
              <View pointerEvents="none" style={{ flex: 1, minWidth: 0 }} />
            </View>
          )}
          <View
            style={{
              flexDirection: 'row',
              gap: 6,
              height: CONTROL_DECK_HEIGHT
            }}
          >
            <View style={{ flex: 4, flexBasis: 18, gap: 8, minWidth: 0 }}>
              <View
                style={{ flexDirection: 'row', gap: 6, height: CONTROL_HEIGHT }}
              >
                {props.moreControl}
                <View style={{ flex: 3, flexBasis: 12 }}>
                  {(props.instruction != null || props.remainingMs != null) &&
                    (props.onFinish != null || props.remainingMs != null) && (
                      <View
                        pointerEvents="box-none"
                        style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: 0,
                          right: 0,
                          alignItems: 'center'
                        }}
                      >
                        <PillButton
                          label={lstrings.common_done}
                          accessibilityLabel={sprintf(
                            lstrings.scoring_bottom_action,
                            props.finishLabel ?? lstrings.scoring_finish_at_bat
                          )}
                          disabled={props.canFinish !== true}
                          onPress={props.onFinish}
                        />
                      </View>
                    )}
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={
                      props.historyLabel == null
                        ? 'Nothing to undo'
                        : `Undo ${props.historyLabel}`
                    }
                    ref={undoButton}
                    disabled={props.historyLabel == null}
                    delayLongPress={500}
                    onPressIn={handleUndoPressIn}
                    onPressOut={handleUndoPressOut}
                    onLongPress={
                      props.onHistoryOpen == null
                        ? undefined
                        : () => {
                            handleUndoLongPress?.()
                            props.onHistoryOpen?.()
                            undoButton.current?.measureInWindow(
                              (x, y, width, height) => {
                                setHistoryBounds({ x, y, width, height })
                              }
                            )
                          }
                    }
                    onPress={() => {
                      props.onUndo()
                    }}
                    style={{
                      ...button,
                      flexDirection: 'row',
                      borderColor: CONTROL_COLORS.undo.border,
                      backgroundColor: CONTROL_COLORS.undo.bottom,
                      paddingHorizontal: 8,
                      opacity: props.historyLabel == null ? 0.5 : 1
                    }}
                  >
                    <ControlBackdrop kind="undo" />
                    <View
                      pointerEvents="none"
                      style={{
                        alignItems: 'center',
                        paddingRight: 8,
                        borderRightWidth: 1,
                        borderRightColor: '#ffffff1a',
                        gap: 2
                      }}
                    >
                      <View
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          backgroundColor: '#273a30',
                          borderWidth: 1,
                          borderColor: '#176448',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <ControlIcon kind="undo" size={14} />
                      </View>
                      <Text
                        selectable={false}
                        style={{
                          color: '#fbbf24',
                          fontFamily: theme.regularTextFont,
                          fontSize: 8,
                          fontWeight: '900',
                          letterSpacing: 1
                        }}
                      >
                        {lstrings.scoring_control_undo}
                      </Text>
                    </View>
                    <View
                      pointerEvents="none"
                      style={{ flex: 1, minWidth: 0, paddingLeft: 8, gap: 3 }}
                    >
                      <UndoTicker
                        enabled={
                          historyBounds == null &&
                          props.suspended !== true &&
                          props.historyLabel != null
                        }
                        testID="scoring-control-label"
                        style={{
                          color: '#ffffff',
                          fontFamily: theme.regularTextFont,
                          fontSize: 12,
                          fontWeight: '900'
                        }}
                        text={
                          props.historyLabel == null
                            ? lstrings.scoring_no_plays
                            : (props.historyDetail?.primary ??
                              props.historyLabel)
                        }
                      />
                      <UndoTicker
                        enabled={
                          historyBounds == null &&
                          props.suspended !== true &&
                          props.historyLabel != null
                        }
                        testID="scoring-undo-description"
                        style={{
                          color: '#cbd5e1',
                          fontFamily: theme.regularTextFont,
                          fontSize: 9
                        }}
                        text={
                          props.historyLabel == null
                            ? lstrings.scoring_history_hint
                            : props.historyDetail == null
                              ? lstrings.scoring_undo_hint
                              : (props.historyDetail.secondary ?? '')
                        }
                      />
                    </View>
                  </Pressable>
                </View>
              </View>
              <View
                style={{ flexDirection: 'row', gap: 6, height: CONTROL_HEIGHT }}
              >
                {PITCH_CONTROLS.filter(control => control.kind !== 'play').map(
                  control => (
                    <Pressable
                      key={control.kind}
                      accessibilityRole="button"
                      accessibilityLabel={control.label}
                      disabled={
                        !props.canPitch ||
                        (props.enabledPitchKinds != null &&
                          !props.enabledPitchKinds.includes(control.kind))
                      }
                      onPress={() => {
                        props.onPitch(control.kind)
                      }}
                      style={{
                        ...button,
                        backgroundColor: CONTROL_COLORS[control.kind].bottom,
                        borderColor: CONTROL_COLORS[control.kind].border,
                        opacity:
                          props.canPitch &&
                          (props.enabledPitchKinds == null ||
                            props.enabledPitchKinds.includes(control.kind))
                            ? 1
                            : 0.5
                      }}
                    >
                      <ControlBackdrop kind={control.kind} />
                      <ControlFace kind={control.kind} />
                    </Pressable>
                  )
                )}
              </View>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              {props.dictationControl != null && (
                <View
                  pointerEvents="box-none"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: CONTROL_DECK_HEIGHT + CONTROL_GAP
                  }}
                >
                  {props.dictationControl}
                </View>
              )}
              {props.ballInPlayControl ?? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Swing in Play"
                  disabled={!props.canPitch}
                  onPress={() => {
                    props.onPitch('play')
                  }}
                  style={{ ...button, opacity: props.canPitch ? 1 : 0.5 }}
                >
                  <ControlBackdrop kind="contact" />
                  <ControlFace kind="contact" contact="swing" />
                </Pressable>
              )}
            </View>
          </View>
        </View>
      </View>
    </View>
  )
}
